/**
 * 안전교육 자료 자동 생성 — 전용 소개 페이지 (대대적 개편판).
 * 14종 법정교육 + 39종 특별교육 · 5개 언어 · 노동청 점검 대비 증빙 · 6종 산출물.
 * 프론트 전용 / 진단 위저드는 클라이언트 사이드 (백엔드 무의존).
 */
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FiBookOpen, FiZap, FiArrowRight, FiCheck, FiX, FiGlobe, FiLayers,
  FiPlayCircle, FiFileText, FiShield, FiAward, FiClock, FiChevronDown,
} from 'react-icons/fi'
import ForceDarkTheme from '../../../components/common/ForceDarkTheme'
import {
  EDU_14, LANGUAGES, OUTPUTS, EVIDENCE,
} from './safetyEducationData'
import LegalEducationDiagnosisWizard from './LegalEducationDiagnosisWizard'
import { SafetyEduDemoResult } from '../../../components/feature/FeatureDemo'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55, ease: [0.21, 0.5, 0.3, 1] as const },
}
const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, delay: i * 0.07 },
})

const METRICS = [
  { v: '14종', l: '법정교육 + 39종 특별교육', s: '별표 4·5 정본 전종' },
  { v: '5개', l: '언어 다국어', s: '한·영·베·중·인니' },
  { v: '6종', l: '산출물 / 1회 생성', s: 'PPTX·영상·퀴즈·일지·자막·ZIP' },
  { v: '₩0', l: '부터 — 외주비 절감', s: '회당 30~50만원 → 무료' },
]

const FAQS = [
  { q: '노동청 점검 증빙은 어떻게 준비되나요?', a: '강사 미출연 사이버 교육의 적법 요건(안전보건교육규정 §3조의2 ②항)인 강사 감수 표기·이해도 평가·강사 의무 비중을 충족하도록 자동 구성합니다. 교육일지(표준 양식)·출석부·평가지를 컴플라이언스 ZIP으로 일괄 보관·제출할 수 있습니다. (최종 적법 여부는 사업장 상황·관할 판단을 확인하세요.)' },
  { q: '강사 없이 AI 영상만 봐도 되나요?', a: '교육 종류에 따라 다릅니다. 정기(사무·판매·현장)·채용시·작업변경·특수형태는 강사 의무 0%로 AI 영상 100% 가능합니다. 관리감독자 정기(강사 50%)·특별교육(67%)은 시스템이 AI편과 강사편을 자동 분할해 강사 의무분을 명시합니다. 건설업 기초(4h)만 집체 의무라 자동 차단됩니다.' },
  { q: '외국인 근로자 교육도 되나요?', a: '베트남어·중국어(간체)·인도네시아어·영어 자막과 음성을 동시 생성할 수 있습니다. 언어별로 자막만/자막+음성/전체(PPTX 번역까지)를 선택할 수 있어 모국어 교육과 점검 증빙을 함께 확보합니다.' },
  { q: '법정 교육 시간(예: 현장직 12시간)은 어떻게 채우나요?', a: '시리즈 모드를 쓰면 법정 시간을 영상 N편으로 자동 분할합니다. 1편 = 법정 1시간(영상 43분 + 휴식)이며, 마지막에 이해도 평가편이 강제로 포함되어 시간·평가 요건을 한 번에 충족합니다.' },
  { q: '회사 로고·브랜딩을 넣을 수 있나요?', a: '회사 로고·브랜딩 적용(PPTX 전 슬라이드 푸터 로고, 강사 감수 슬라이드의 강사명·자격·감수일, 수료증의 회사명·강사 서명 반영 등)은 협의를 통해 엔터프라이즈 플랜에서 제공됩니다. 도입 문의 주시면 맞춤 적용을 안내해 드립니다.' },
]

const Section = ({ id, children, className = '' }: { id?: string; children: React.ReactNode; className?: string }) => (
  <section id={id} className={`py-20 md:py-28 border-t border-white/[0.06] ${className}`}>
    <div className="max-w-6xl mx-auto px-6 lg:px-8">{children}</div>
  </section>
)
const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-purple-300 mb-3">{children}</span>
)
const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">{children}</h2>
)

/** 생성된 슬라이드 미리보기 — 실제 산출물 느낌의 CSS 목업 */
function SlideMock() {
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0f1424] shadow-2xl aspect-video flex flex-col">
      <div className="px-6 pt-6 pb-3">
        <div className="text-[10px] tracking-widest text-purple-300 font-semibold mb-1.5">CHAPTER 03 · 산업안전 및 사고 예방</div>
        <div className="text-white font-bold text-lg leading-tight">작업 전 안전점검 5요소와 사고 예방 4원칙</div>
      </div>
      <div className="flex-1 grid grid-cols-5 gap-3 px-6 pb-4">
        <ul className="col-span-3 space-y-2 text-[11px] text-zinc-300">
          {['보호구 착용 상태 확인 (KOSHA Guide G-82)', '작업 동선·추락 위험 구간 점검', '비상정지·소화설비 위치 숙지', '유해인자 표지(MSDS) 게시 확인'].map((b) => (
            <li key={b} className="flex gap-1.5"><span className="text-emerald-400 mt-0.5">✓</span><span>{b}</span></li>
          ))}
          <li className="mt-2 inline-flex items-center gap-1 rounded bg-amber-400/15 text-amber-300 px-2 py-1 text-[10px] font-medium">⚖ 산안법 §38·§40 근거</li>
        </ul>
        <div className="col-span-2 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
          <div className="text-center">
            <div className="text-3xl mb-1">🦺</div>
            <div className="text-[9px] text-zinc-500">AI 생성 현장 일러스트</div>
          </div>
        </div>
      </div>
      <div className="px-6 py-2 border-t border-white/10 flex items-center justify-between text-[9px] text-zinc-500">
        <span className="font-serif text-zinc-300">Heinrich · 다산AI 안전교육 자동생성</span>
        <span>12 / 28</span>
      </div>
    </div>
  )
}

export default function SafetyEducationFeature() {
  return (
    <div className="bg-zinc-950 text-white">
      <ForceDarkTheme />
      <Helmet>
        <title>법정 안전교육 자동 생성 — PPTX·영상·퀴즈·교육일지 | Heinrich</title>
        <meta name="description" content="산안법 14종 법정교육 + 39종 특별교육을 5개 언어로. 강사 감수·이해도 평가·교육일지까지 PPTX·강의실 영상·퀴즈로 자동 생성. 외주 수백만원 → ₩0부터. 1분 진단으로 우리 회사 의무 교육·과태료 확인." />
        <meta property="og:title" content="법정 안전교육 자료, AI로 빠르게 — ₩0부터" />
        <meta property="og:description" content="14종 법정교육 + 39종 특별교육 · 5개 언어 · 영상·PPTX·퀴즈·교육일지·컴플라이언스 ZIP 자동 생성." />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'SoftwareApplication',
          name: 'Heinrich 안전교육 자동 생성', applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web', offers: { '@type': 'Offer', price: '0', priceCurrency: 'KRW' },
          description: '산안법 14종 법정교육 + 39종 특별교육을 PPTX·영상·퀴즈·교육일지로 자동 생성.',
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        })}</script>
      </Helmet>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 right-0 w-[44rem] h-[44rem] bg-purple-600/15 rounded-full blur-3xl" />
          <div className="absolute top-40 -left-20 w-[30rem] h-[30rem] bg-fuchsia-600/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/15 border border-purple-500/30 text-purple-200 mb-6">
              <FiBookOpen size={13} /> 법정 안전교육 자료 제작 플랫폼
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[3.4rem] font-black leading-[1.1] tracking-tight">
              법정 안전교육 자료,<br />
              <span className="bg-gradient-to-r from-purple-300 to-fuchsia-300 bg-clip-text text-transparent">AI로 빠르게 만듭니다.</span>
            </h1>
            <p className="mt-6 text-[15px] md:text-base text-zinc-400 leading-[1.8] max-w-xl">
              산안법 <b className="text-zinc-200">14종 법정교육 + 39종 특별교육</b>을 <b className="text-zinc-200">5개 언어</b>로.
              강사 감수·이해도 평가·교육일지까지 <b className="text-zinc-200">PPTX·영상·퀴즈</b>로 한 번에 생성합니다.
              외주 수백만원 → <b className="text-purple-300">₩0부터.</b>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#diagnosis" className="inline-flex items-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-6 py-3.5 font-bold transition-colors">
                <FiZap size={18} /> 1분 진단 — 우리 회사 의무·과태료
              </a>
              <Link to="/dashboard/safety-education" className="inline-flex items-center gap-2 rounded-xl border border-white/15 hover:bg-white/5 px-6 py-3.5 font-semibold transition-colors">
                무료로 1편 생성 <FiArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-400" /> 강사 감수·평가 자동</span>
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-400" /> 교육일지·출석부 PDF</span>
              <span className="flex items-center gap-1.5"><FiCheck className="text-emerald-400" /> 컴플라이언스 ZIP</span>
            </div>
          </motion.div>
          <motion.div {...stagger(2)} className="relative">
            <SlideMock />
            <div className="absolute -bottom-5 -right-3 rounded-xl border border-white/10 bg-zinc-900 px-4 py-2.5 shadow-xl flex items-center gap-2">
              <FiPlayCircle className="text-purple-300" size={20} />
              <div>
                <div className="text-xs font-bold text-white">영상 · 43분 / 편</div>
                <div className="text-[10px] text-zinc-500">AI 음성 · 자막 · BGM</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── METRICS ── */}
      <section className="border-y border-white/[0.06] bg-zinc-900/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/[0.06]">
          {METRICS.map((m, i) => (
            <motion.div key={m.l} {...stagger(i)} className="px-5 py-8 text-center">
              <div className="text-3xl md:text-4xl font-black text-white">{m.v}</div>
              <div className="text-sm font-medium text-zinc-300 mt-1">{m.l}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{m.s}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── 진단 위저드 ── */}
      <Section id="diagnosis">
        <motion.div {...fadeUp} className="text-center mb-10 max-w-2xl mx-auto">
          <Eyebrow>1분 무료 진단</Eyebrow>
          <H2>우리 회사가 받아야 할 교육과<br />미실시 과태료를 지금 확인하세요</H2>
          <p className="mt-4 text-zinc-400 text-[15px] leading-relaxed">
            근로자 구성만 입력하면 적용되는 법정 교육과 1차 적발 과태료(별표 35)를 즉시 산출합니다. 가입 없이 바로 사용할 수 있습니다.
          </p>
        </motion.div>
        <motion.div {...fadeUp}><LegalEducationDiagnosisWizard /></motion.div>
      </Section>

      {/* ── 6종 산출물 ── */}
      <Section>
        <motion.div {...fadeUp} className="text-center mb-12">
          <Eyebrow>한 번 생성, 6종 산출물</Eyebrow>
          <H2>강의자료부터 노동청 제출 패키지까지</H2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {OUTPUTS.map((o, i) => (
            <motion.div key={o.title} {...stagger(i)}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 hover:border-purple-500/30 transition-colors">
              <div className="text-3xl mb-3">{o.icon}</div>
              <div className="font-bold text-white text-[15px]">{o.title}</div>
              <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{o.desc}</div>
              <div className="mt-3 inline-block text-[11px] text-purple-300 border border-purple-500/30 rounded-full px-2.5 py-0.5">{o.use}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── 실제 생성 결과물 미리보기 (슬라이드 PDF · NAS 영상 · 문제집) ── */}
      <Section className="bg-zinc-900/30">
        <motion.div {...fadeUp} className="text-center mb-10">
          <Eyebrow>실제 생성 결과물</Eyebrow>
          <H2>AI가 만든 교육 자료를 지금 직접 확인하세요</H2>
          <p className="mt-3 text-zinc-400 text-[15px]">
            건설업 근로자 정기교육(2분기) 예시 — 실제 생성된 슬라이드 PDF · 강의실 영상 강의 · 이해도 평가 문제집입니다.
          </p>
        </motion.div>
        <motion.div {...fadeUp}
          className="rounded-2xl border border-white/[0.10] bg-[#0f1424] overflow-hidden shadow-2xl">
          <SafetyEduDemoResult
            data={{
              education_type: '근로자 정기교육 (현장직)',
              legal_hours: 12,
              title: '건설업 근로자 정기 안전보건교육 (2분기)',
              video_info: { duration_min: 43, tts: 'Gemini Chirp 3 HD' },
              quiz: { total: 5 },
            }}
          />
        </motion.div>
      </Section>

      {/* ── 법정 대체 매트릭스 ── */}
      <Section className="bg-zinc-900/30">
        <motion.div {...fadeUp} className="text-center mb-10">
          <Eyebrow>합법 대체 가능 범위</Eyebrow>
          <H2>14종 법정교육 중 13종, AI로 대체</H2>
          <p className="mt-3 text-zinc-400 text-[15px]">집체 의무인 건설업 기초 1종만 자동 차단 — 과장 없이 정직하게 표시합니다.</p>
        </motion.div>
        <motion.div {...fadeUp} className="overflow-x-auto rounded-2xl border border-white/[0.08]">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/[0.03] text-zinc-400 text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-3">교육 종류</th>
                <th className="font-medium px-3 py-3">법정 시간</th>
                <th className="font-medium px-3 py-3">주기</th>
                <th className="font-medium px-3 py-3">강사 의무</th>
                <th className="font-medium px-3 py-3">AI 대체</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {EDU_14.map((r) => (
                <tr key={r.nameKo} className={r.aiEligible ? '' : 'bg-amber-500/[0.04]'}>
                  <td className="px-4 py-2.5 text-zinc-200">{r.nameKo}</td>
                  <td className="px-3 py-2.5 text-center text-zinc-400">{r.hours}</td>
                  <td className="px-3 py-2.5 text-center text-zinc-500 text-xs">{r.cycle}</td>
                  <td className="px-3 py-2.5 text-center text-zinc-400 text-xs">{r.instructor}</td>
                  <td className="px-3 py-2.5 text-center">
                    {r.aiEligible
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 font-medium"><FiCheck size={14} /> 가능</span>
                      : <span className="inline-flex items-center gap-1 text-amber-400 font-medium"><FiX size={14} /> 집체 의무</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </Section>

      {/* ── 다국어 쇼케이스 ── */}
      <Section>
        <motion.div {...fadeUp} className="text-center mb-12">
          <Eyebrow><FiGlobe className="inline mb-0.5" /> 외국인 근로자 대응</Eyebrow>
          <H2>같은 교육을 모국어로 — 5개 언어</H2>
          <p className="mt-3 text-zinc-400 text-[15px]">자막만 / 자막+음성 / PPTX 번역까지 — 언어별 출력 모드를 선택할 수 있습니다.</p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {LANGUAGES.map((l, i) => (
            <motion.div key={l.code} {...stagger(i)}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 text-center hover:border-sky-500/30 transition-colors">
              <div className="text-4xl mb-2">{l.flag}</div>
              <div className="font-bold text-white text-sm">{l.label}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{l.note}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── 시리즈 = 법정 시간 자동 충족 ── */}
      <Section className="bg-zinc-900/30">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <Eyebrow><FiLayers className="inline mb-0.5" /> 시리즈 모드</Eyebrow>
            <H2>법정 시간을 영상 편수로<br />자동으로 정확히 채웁니다</H2>
            <p className="mt-4 text-zinc-400 text-[15px] leading-relaxed">
              예) 현장직 정기교육은 반기 <b className="text-white">12시간</b>. 시리즈 모드가 이를 <b className="text-purple-300">12편</b>으로
              자동 분할하고, 마지막에 <b className="text-white">이해도 평가편</b>을 강제 포함합니다.
              관리감독자·특별교육은 강사 의무분을 별도 강사편으로 분리해 표시합니다.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-zinc-300">
              {['법정 시간 = 영상 N편 자동 분할 (1편 = 1시간)', '평가편 강제 포함 (사이버 교육 평가 요건)', '강사 의무편 자동 분리 (50%·67%)', '전 편 + 일지 + 출석부 컴플라이언스 ZIP'].map((x) => (
                <li key={x} className="flex gap-2"><FiCheck className="text-emerald-400 mt-0.5 shrink-0" size={15} />{x}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="rounded-2xl border border-white/[0.08] bg-[#0f1424] p-6">
            <div className="text-xs text-zinc-500 mb-3">현장직 정기교육 (반기 12h)</div>
            <div className="grid grid-cols-4 gap-2">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="rounded-lg bg-purple-500/15 border border-purple-500/30 py-3 text-center">
                  <div className="text-[10px] text-purple-200">EP{i + 1}</div>
                  <div className="text-[9px] text-zinc-500">43분</div>
                </div>
              ))}
              <div className="col-span-4 rounded-lg bg-emerald-500/15 border border-emerald-500/40 py-2.5 text-center text-xs text-emerald-300 font-medium">
                + 이해도 평가편 (퀴즈 19~25문항)
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ── 노동청 점검 대비 증빙 ── */}
      <Section>
        <motion.div {...fadeUp} className="text-center mb-12">
          <Eyebrow><FiShield className="inline mb-0.5" /> 노동청 점검 대비</Eyebrow>
          <H2>"적법한 사이버 교육"의 요건을 자동 충족</H2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-4">
          {EVIDENCE.map((e, i) => (
            <motion.div key={e.title} {...stagger(i)}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 flex gap-4">
              <div className="text-2xl shrink-0">{e.icon}</div>
              <div>
                <div className="font-bold text-white text-[15px]">{e.title}</div>
                <div className="text-[11px] text-purple-300 mt-0.5">{e.law}</div>
                <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{e.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── 워크플로우 ── */}
      <Section className="bg-zinc-900/30">
        <motion.div {...fadeUp} className="text-center mb-12">
          <Eyebrow>생성 4단계</Eyebrow>
          <H2>입력 3분, 결과물은 자동으로</H2>
        </motion.div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: '01', t: '교육 유형 선택', d: '14종 법정교육 또는 39종 특별교육 중 선택. 강사 정보·언어 옵션 입력.', icon: <FiBookOpen /> },
            { n: '02', t: '현장 정보 입력', d: '업종·현장 위험요인 입력 (사업장 등록 시 자동 채움).', icon: <FiFileText /> },
            { n: '03', t: 'AI 생성', d: 'PPTX·문제·해설 먼저 즉시 다운로드, 강의실 영상 강의는 백그라운드 생성 + 완료 메일 알림.', icon: <FiZap /> },
            { n: '04', t: '수강·출석', d: 'PPTX·퀴즈·일지 다운로드, 영상 강의는 강의실에서 수강. QR로 모바일 출석 기록.', icon: <FiAward /> },
          ].map((s, i) => (
            <motion.div key={s.n} {...stagger(i)} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-2xl font-black text-purple-300/40">{s.n}</span>
                <span className="text-purple-300 text-xl">{s.icon}</span>
              </div>
              <div className="font-bold text-white">{s.t}</div>
              <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{s.d}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── 비교 ── */}
      <Section>
        <motion.div {...fadeUp} className="text-center mb-12">
          <Eyebrow>왜 Heinrich인가</Eyebrow>
          <H2>외주 강사 · 일반 템플릿과 무엇이 다른가</H2>
        </motion.div>
        <motion.div {...fadeUp} className="overflow-x-auto rounded-2xl border border-white/[0.08]">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/[0.03] text-zinc-400 text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-3">항목</th>
                <th className="font-medium px-3 py-3">외주 강사</th>
                <th className="font-medium px-3 py-3">일반 PPT 템플릿</th>
                <th className="font-medium px-3 py-3 text-purple-300">Heinrich</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {[
                ['비용', '회당 30~50만원', '제작 인건비', '₩0부터'],
                ['법정 내용 정확도', '강사 역량 의존', '직접 작성 필요', '별표5 항목 1:1 매핑'],
                ['노동청 증빙', '수기 일지', '없음', '일지·평가·ZIP 자동'],
                ['다국어', '추가 비용', '직접 번역', '5개 언어 자동'],
                ['소요 시간', '일정 조율', '수일', 'PPTX 수분 · 영상 자동'],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-2.5 text-zinc-300 font-medium">{row[0]}</td>
                  <td className="px-3 py-2.5 text-center text-zinc-500">{row[1]}</td>
                  <td className="px-3 py-2.5 text-center text-zinc-500">{row[2]}</td>
                  <td className="px-3 py-2.5 text-center text-white font-medium bg-purple-500/[0.06]">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </Section>

      {/* ── FAQ ── */}
      <Section className="bg-zinc-900/30">
        <motion.div {...fadeUp} className="text-center mb-10">
          <Eyebrow>자주 묻는 질문</Eyebrow>
          <H2>가장 많이 궁금해하시는 것</H2>
        </motion.div>
        <div className="max-w-3xl mx-auto space-y-3">
          {FAQS.map((f, i) => (
            <motion.details key={i} {...stagger(i)}
              className="group rounded-2xl border border-white/[0.08] bg-white/[0.02] px-5 py-4 [&_summary]:cursor-pointer">
              <summary className="flex items-center justify-between gap-3 list-none">
                <span className="font-semibold text-white text-[15px]">{f.q}</span>
                <FiChevronDown className="text-zinc-500 group-open:rotate-180 transition-transform shrink-0" />
              </summary>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </motion.details>
          ))}
        </div>
      </Section>

      {/* ── 최종 CTA ── */}
      <section className="relative overflow-hidden py-24 border-t border-white/[0.06]">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-purple-600/15 rounded-full blur-3xl" />
        </div>
        <motion.div {...fadeUp} className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            지금 1분 진단으로<br />과태료 위험부터 확인하세요
          </h2>
          <p className="mt-4 text-zinc-400 text-[15px]">진단은 무료, 가입 없이 바로. 마음에 들면 첫 1편은 무료로 생성해 보세요.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#diagnosis" className="inline-flex items-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-7 py-3.5 font-bold transition-colors">
              <FiZap size={18} /> 무료 진단 시작
            </a>
            <Link to="/dashboard/safety-education" className="inline-flex items-center gap-2 rounded-xl border border-white/15 hover:bg-white/5 px-7 py-3.5 font-semibold transition-colors">
              무료로 1편 생성 <FiArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5"><FiClock className="text-purple-300" /> PPTX 수분 내</span>
            <span className="flex items-center gap-1.5"><FiGlobe className="text-purple-300" /> 5개 언어</span>
            <span className="flex items-center gap-1.5"><FiShield className="text-purple-300" /> 노동청 점검 대비 증빙</span>
          </div>
        </motion.div>
      </section>
    </div>
  )
}
